import { p as proxyCustomElement, H, c as createEvent, h, d as Host } from './p-bfa0dc67.js';
import { c as classNames } from './p-df8c8ec2.js';
import { n as nanoid } from './p-5959f2bd.js';
import { i as inheritAriaAttributes } from './p-198613ef.js';

const biRadiobuttonCss = ".sr-only{position:absolute;height:1px;width:1px;padding:0;margin:-1px;overflow:hidden;border:0;left:-9999px;text-indent:-5000px;clip:rect(1px, 1px, 1px, 1px);}*,*:before,*:after{box-sizing:border-box}html,body,div,span,object,iframe,figure,h1,h2,h3,h4,h5,h6,p,blockquote,pre,a,code,em,img,small,strike,strong,sub,sup,tt,b,u,i,ol,ul,li,fieldset,form,label,table,caption,tbody,tfoot,thead,tr,th,td,main,canvas,embed,footer,header,nav,section,button,video{margin:0;padding:0;border:0;font-size:100%;font:inherit;vertical-align:baseline;text-rendering:optimizeLegibility;-webkit-font-smoothing:antialiased;text-size-adjust:none}footer,header,nav,section,main{display:block}body{line-height:1.5}ol,ul{list-style:none}blockquote,q{quotes:none}blockquote:before,blockquote:after,q:before,q:after{content:\"\";content:none}table{border-collapse:collapse;border-spacing:0}input{-webkit-appearance:none;appearance:none;border-radius:0}:host(:focus){outline:none !important;border:none !important}:host{width:fit-content}:host(:focus){outline:none;border:none}.wrapper{display:flex;flex-direction:column;padding-left:2px}.wrapper.disabled{opacity:0.3;cursor:not-allowed}.container{font-family:\"museo-sans\", arial, sans-serif;font-weight:300;font-size:1rem;line-height:1.5;position:relative;align-items:center;display:flex;gap:0.5rem;user-select:none;width:fit-content;min-height:1.5rem;border:2px dashed transparent;cursor:pointer;--text-color:#414651;--bg-hover-color:rgba(1, 65, 127, 0.1019607843);--border-color:#01417b;--border-error-color:#8c0a08;--checkmark-color:#01417b;--required-color:#8c0a08;color:var(--text-color)}.container:has(input:disabled){cursor:default}.container.dark{color-scheme:dark;--text-color:whitesmoke;--bg-hover-color:rgba(1, 65, 127, 0.2509803922);--border-color:#78c2ff;--border-error-color:#ec9a98;--checkmark-color:#78c2ff;--required-color:#ec9a98}.container:focus-within{border-radius:4px;outline-offset:2px;outline:1.5px solid var(--bi-primary-50);box-shadow:0 0 0 2px white}.container .required{color:var(--required-color)}.container input{position:absolute;opacity:0;cursor:pointer;height:0;width:0}.container .checkmark{position:relative;display:block;height:1.25rem;width:1.25rem;flex-shrink:0;border:2px solid var(--border-color);border-radius:100%}.container .checkmark::after{content:\"\";position:absolute;display:block;top:50%;left:50%;transform:translate(-50%, -50%);width:5px;height:0;width:0;border-radius:100%;background-color:var(--checkmark-color);transition:width 0.1s ease-in-out, height 0.1s ease-in-out}.container .checkmark.invalid{border-color:var(--border-error-color)}.container .checkmark.invalid::after{background-color:var(--border-error-color)}.container:hover input~.checkmark{background-color:var(--bg-hover-color)}.container input:checked~.checkmark:after{height:12px;width:12px}";
const BiRadioButtonStyle0 = biRadiobuttonCss;

const BiRadioButton$1 = /*@__PURE__*/ proxyCustomElement(class BiRadioButton extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.change = createEvent(this, "change", 7);
        this.blur = createEvent(this, "blur", 7);
        this.internals = this.attachInternals?.();
        this.inheritedAttributes = {};
        /** Marks the radio button field as required. */
        this.required = false;
        /** Marks the radio button field as disabled. */
        this.disabled = false;
        /**
         * If `true`, the component will indicate an error.
         * @default false
         */
        this.error = false;
        /** Is the component displayed on a dark background. */
        this.dark = false;
        this.group = {
            hosts: [],
            members: [],
            first: null,
            last: null,
            checked: null,
            focused: null,
        };
        this.onBlur = (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.blur.emit(e);
        };
        this.KEYCODES = {
            SPACE: 'Space',
            LEFT: 'ArrowLeft',
            UP: 'ArrowUp',
            RIGHT: 'ArrowRight',
            DOWN: 'ArrowDown',
        };
        this.controlChangeHandler = (e) => {
            e.stopPropagation();
            this.groupCollectMembers();
            this.groupSetChecked(this.radioInputElement, e);
            this.controlSetChecked(this.radioInputElement.checked, e);
        };
        // https://googlechromelabs.github.io/howto-components/howto-radio-group/
        this.controlKeyDownHandler = (e) => {
            e.stopPropagation();
            if (Object.values(this.KEYCODES).includes(e.code))
                e.preventDefault();
            this.groupCollectMembers();
            switch (e.code) {
                case this.KEYCODES.UP:
                case this.KEYCODES.LEFT:
                    this.groupSetChecked(this.groupGetPrev(), e);
                    break;
                case this.KEYCODES.DOWN:
                case this.KEYCODES.RIGHT:
                    this.groupSetChecked(this.groupGetNext(), e);
                    break;
                case this.KEYCODES.SPACE:
                    this.groupSetChecked(this.radioInputElement, e);
                    break;
            }
        };
        this.controlSetChecked = (checked, e) => {
            if (!this.radioInputElement)
                return;
            if (this.disabled) {
                this.internals?.setFormValue(null);
            }
            else {
                this.radioInputElement.checked = this.checked = checked;
                this.internals?.setFormValue(this.checked ? this.radioInputElement.value : null);
                if (e) {
                    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/radio
                    // if an event parameter is given and a native control would fire an event, emit the corresponding event to the light dom
                    if (this.checked && e.type === 'change')
                        this.change.emit(e);
                }
            }
        };
        this.groupCollectMembers = () => {
            if (this.name) {
                const memberSelector = `bi-radio-button[name="${this.name}"]`;
                const form = this.internals?.form ?? this.el.closest('form');
                if (form) {
                    // We only want to find other radio buttons in the same form if we're inside a form.
                    this.group.hosts = Array.from(form.querySelectorAll(memberSelector));
                }
                else {
                    // If we're not in a form, we want to find all radio buttons with the same name that aren't inside a form.
                    this.group.hosts = Array.from(document.querySelectorAll(`${memberSelector}:not(form ${memberSelector})`));
                }
                this.group.members = this.group.hosts
                    .map((m) => m.shadowRoot.querySelector('input[type="radio"]'))
                    .filter((m) => m !== null);
                if (this.group.members.length > 1) {
                    this.group.first = this.group.members[0];
                    this.group.last = this.group.members[this.group.members.length - 1];
                    this.group.checked = this.group.members.find((m) => m.checked) ?? null;
                    this.group.focused =
                        this.group.members.find((m) => m.getRootNode().host === document.activeElement) ??
                            this.group.first;
                    const focusableMember = this.group.checked || this.group.focused || this.group.first;
                    this.group.members.forEach((m) => {
                        m.tabIndex = m === focusableMember ? 0 : -1;
                    });
                }
            }
        };
        this.groupGetPrev = () => {
            const focusedIndex = this.group.members.findIndex((m) => m.id === this.group.focused?.id);
            return this.group.members.find((_m, i) => i === focusedIndex - 1) ?? this.group.last;
        };
        this.groupGetNext = () => {
            const focusedIndex = this.group.members.findIndex((m) => m.id === this.group.focused?.id);
            return this.group.members.find((_m, i) => i === focusedIndex + 1) ?? this.group.first;
        };
        this.groupSetChecked = (newChecked, e) => {
            if (this.group.members.length >= 1) {
                const isKeyboardEvent = e.type === 'keydown';
                const newIsAriaDisabled = newChecked.getAttribute('aria-disabled') === String(true);
                const newIndex = this.group.members.findIndex((m) => m === newChecked);
                if (isKeyboardEvent)
                    newChecked.focus();
                // if new is disabled, do not reset/set anything
                if (!newIsAriaDisabled) {
                    // reset all group members but the newChecked
                    this.group.hosts.filter((_h, i) => i !== newIndex).forEach((h) => h.groupReset());
                    // if method was called by keyboard event, select newChecked
                    // else this has already been done by clicking on the newChecked element already
                    if (isKeyboardEvent)
                        newChecked.click();
                }
            }
        };
    }
    get type() {
        return 'radio';
    }
    /**
     * A hidden method to reset the group controls `checked` state to `false`.
     */
    async groupReset() {
        if (this.disabled)
            this.radioInputElement.checked = this.checked = false;
        this.controlSetChecked(false);
    }
    updateControlChecked(checked = this.checked) {
        this.controlSetChecked(checked);
    }
    updateControlDisabled() {
        this.controlSetChecked(this.checked);
    }
    componentWillLoad() {
        if (this.checked !== undefined) {
            this.defaultChecked = this.checked;
        }
        else {
            this.checked = this.defaultChecked ?? false;
        }
        this.inheritedAttributes = inheritAriaAttributes(this.el);
    }
    componentDidLoad() {
        if (this.radioInputElement.checked) {
            this.internals?.setFormValue(this.value);
        }
    }
    componentDidRender() {
        this.groupCollectMembers();
    }
    formStateRestoreCallback(checked) {
        this.controlSetChecked(checked);
    }
    formResetCallback() {
        this.controlSetChecked(this.defaultChecked);
    }
    render() {
        const id = `radio_button-${nanoid()}`;
        return (h(Host, { key: '2d780e76ca4d8f0573a9e64472b2d689ad7a3de5' }, h("div", { key: 'f2a37d14ac33b6f1588fb17a889a4de2232bab8e', class: classNames('wrapper', this.disabled && 'disabled') }, h("label", { key: '2e02bdbcfdb722567e1abbe49b3ad879d85dca68', class: classNames('container', this.dark && 'dark') }, h("input", { key: 'b0d081ac0cbbceed5b6a86c0f99562970204d9e1', type: 'radio', id: id, "aria-invalid": String(this.error), "aria-disabled": String(this.disabled), checked: this.checked, value: this.value, name: this.name, onChange: this.controlChangeHandler, onBlur: this.onBlur, onKeyDown: this.controlKeyDownHandler, required: this.required, disabled: this.disabled, ref: (el) => (this.radioInputElement = el), ...this.inheritedAttributes }), h("span", { key: 'd7845492d1a8d872e1b65171ef7142451ad67fc3', class: classNames('checkmark', this.error && 'invalid') }), this.label ? (h("p", null, this.label, ' ', this.required && (h("span", { class: 'required', "aria-hidden": 'true' }, ' ', "*")))) : (h("slot", null))))));
    }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    get el() { return this; }
    static get watchers() { return {
        "checked": ["updateControlChecked"],
        "disabled": ["updateControlDisabled"]
    }; }
    static get style() { return BiRadioButtonStyle0; }
}, [81, "bi-radio-button", {
        "type": [2049],
        "label": [1],
        "name": [513],
        "value": [1],
        "required": [4],
        "disabled": [4],
        "defaultChecked": [4, "default-checked"],
        "checked": [1028],
        "error": [4],
        "dark": [4],
        "groupReset": [64]
    }, undefined, {
        "checked": ["updateControlChecked"],
        "disabled": ["updateControlDisabled"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["bi-radio-button"];
    components.forEach(tagName => { switch (tagName) {
        case "bi-radio-button":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BiRadioButton$1);
            }
            break;
    } });
}

const BiRadioButton = BiRadioButton$1;
const defineCustomElement = defineCustomElement$1;

export { BiRadioButton, defineCustomElement };
